import React, { useCallback, useEffect, useState } from 'react';
import { Database, RefreshCw, Search, Trash2, Save, X, Plus, Clock, AlertTriangle } from 'lucide-react';
import { adminService, RedisKeyInfo, RedisStatsResponse, RedisValueResponse } from '../services/AdminService';

// Admin Redis browser. Listing is cursor-based SCAN (the backend never runs a
// blocking KEYS *), so "Load more" walks the cursor returned by the previous
// page. Editing is limited to string values; collections are shown read-only.
const PAGE_COUNT = 100;

const bytesLabel = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GiB`;
};

const ttlLabel = (ttl: number): string => {
    if (ttl === -1) return 'no expiry';
    if (ttl === -2) return 'missing';
    if (ttl < 60) return `${ttl}s`;
    if (ttl < 3600) return `${Math.round(ttl / 60)}m`;
    if (ttl < 86400) return `${Math.round(ttl / 3600)}h`;
    return `${Math.round(ttl / 86400)}d`;
};

const RedisTab: React.FC = () => {
    const [pattern, setPattern] = useState('*');
    const [patternDraft, setPatternDraft] = useState('*');
    const [keys, setKeys] = useState<RedisKeyInfo[]>([]);
    const [cursor, setCursor] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [dbsize, setDbsize] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [stats, setStats] = useState<RedisStatsResponse | null>(null);
    const [statsLoading, setStatsLoading] = useState(false);

    // Selected key detail / editor
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [detail, setDetail] = useState<RedisValueResponse | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [editValue, setEditValue] = useState('');
    const [editTtl, setEditTtl] = useState<string>('');
    const [saving, setSaving] = useState(false);
    const [actionMsg, setActionMsg] = useState<string | null>(null);

    // Create-new-key form
    const [showCreate, setShowCreate] = useState(false);
    const [newKey, setNewKey] = useState('');

    const scan = useCallback(async (resetCursor: boolean, pat?: string) => {
        const usePattern = pat ?? pattern;
        const useCursor = resetCursor ? 0 : cursor;
        setLoading(true);
        setError(null);
        try {
            const resp = await adminService.scanRedisKeys({ pattern: usePattern, cursor: useCursor, count: PAGE_COUNT });
            const data = resp.data;
            setKeys(prev => (resetCursor ? data.keys : [...prev, ...data.keys]));
            setCursor(data.cursor);
            setHasMore(data.has_more);
            setDbsize(data.dbsize);
        } catch (e: any) {
            setError(e?.response?.data?.detail || e?.message || 'Failed to scan keys');
        } finally {
            setLoading(false);
        }
    }, [pattern, cursor]);

    const loadStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const resp = await adminService.getRedisStats();
            setStats(resp.data);
        } catch (e: any) {
            setError(e?.response?.data?.detail || e?.message || 'Failed to load Redis statistics');
        } finally {
            setStatsLoading(false);
        }
    }, []);

    // Initial load
    useEffect(() => {
        scan(true, '*');
        loadStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const runSearch = () => {
        setPattern(patternDraft || '*');
        setSelectedKey(null);
        setDetail(null);
        scan(true, patternDraft || '*');
    };

    const openKey = useCallback(async (key: string) => {
        setSelectedKey(key);
        setDetail(null);
        setActionMsg(null);
        setDetailLoading(true);
        try {
            const resp = await adminService.getRedisValue(key);
            setDetail(resp.data);
            if (resp.data.editable && resp.data.encoding === 'utf-8') {
                setEditValue(typeof resp.data.value === 'string' ? resp.data.value : '');
            } else {
                setEditValue('');
            }
            setEditTtl(resp.data.ttl > 0 ? String(resp.data.ttl) : '');
        } catch (e: any) {
            setActionMsg(e?.response?.data?.detail || 'Failed to load value');
        } finally {
            setDetailLoading(false);
        }
    }, []);

    const handleSave = async () => {
        if (!selectedKey) return;
        setSaving(true);
        setActionMsg(null);
        try {
            const ttlNum = editTtl.trim() === '' ? null : parseInt(editTtl, 10);
            await adminService.setRedisValue({ key: selectedKey, value: editValue, ttl: ttlNum });
            setActionMsg('Saved.');
            await openKey(selectedKey);
        } catch (e: any) {
            setActionMsg(e?.response?.data?.detail || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (key: string) => {
        if (!window.confirm(`Delete Redis key?\n\n${key}\n\nThis cannot be undone.`)) return;
        try {
            await adminService.deleteRedisKey(key);
            setKeys(prev => prev.filter(k => k.key !== key));
            if (selectedKey === key) {
                setSelectedKey(null);
                setDetail(null);
            }
        } catch (e: any) {
            setError(e?.response?.data?.detail || 'Failed to delete key');
        }
    };

    const handleCreate = () => {
        const k = newKey.trim();
        if (!k) return;
        setShowCreate(false);
        setNewKey('');
        // Open a blank editor for the new key; saving creates it.
        setSelectedKey(k);
        setDetail({
            status: 'success', key: k, type: 'string', ttl: -1,
            editable: true, value: '', encoding: 'utf-8', truncated: false,
        });
        setEditValue('');
        setEditTtl('');
        setActionMsg('New key — set a value and Save to create it.');
    };

    const renderValue = () => {
        if (!detail) return null;
        if (detail.editable) {
            if (detail.encoding === 'base64') {
                return (
                    <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                        Binary value (base64, read-only). Editing binary values is not supported here.
                        <pre className="mt-2 whitespace-pre-wrap break-all text-xs text-gray-700">{detail.value}</pre>
                    </div>
                );
            }
            return (
                <textarea
                    value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    className="w-full h-64 font-mono text-xs border border-gray-300 rounded p-2 focus:ring-1 focus:ring-blue-500"
                    spellCheck={false}
                />
            );
        }
        // Collections / unsupported — read-only JSON view.
        return (
            <pre className="w-full max-h-64 overflow-auto bg-gray-50 border border-gray-200 rounded p-3 text-xs whitespace-pre-wrap break-all">
                {detail.encoding === 'unsupported'
                    ? `(type "${detail.type}" cannot be displayed)`
                    : JSON.stringify(detail.value, null, 2)}
            </pre>
        );
    };

    return (
        <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-medium text-gray-900 flex items-center">
                        <Database className="w-5 h-5 mr-2" />
                        Redis Browser
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Inspect, edit and delete cache keys.{dbsize != null && <> Total keys in DB: <span className="font-medium">{dbsize.toLocaleString()}</span>.</>}
                    </p>
                </div>
                <button
                    onClick={() => setShowCreate(true)}
                    className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-1"
                >
                    <Plus className="w-4 h-4" /> New key
                </button>
            </div>

            {stats && (
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <h3 className="text-sm font-medium text-gray-900">Production health</h3>
                            <p className="text-xs text-gray-500">
                                Prefix memory is {stats.sample_complete ? 'complete' : 'sampled'} across {stats.sampled_keys.toLocaleString()} key(s).
                            </p>
                        </div>
                        <button onClick={loadStats} disabled={statsLoading} className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50 flex items-center gap-1">
                            <RefreshCw className={`w-3.5 h-3.5 ${statsLoading ? 'animate-spin' : ''}`} /> Refresh metrics
                        </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                        <div><div className="text-xs text-gray-500">Memory used</div><div className="text-sm font-medium">{bytesLabel(stats.memory.used_bytes)}</div></div>
                        <div><div className="text-xs text-gray-500">Operations/sec</div><div className="text-sm font-medium">{stats.traffic.operations_per_second.toLocaleString()}</div></div>
                        <div><div className="text-xs text-gray-500">Connected clients</div><div className="text-sm font-medium">{stats.clients.connected.toLocaleString()}</div></div>
                        <div><div className="text-xs text-gray-500">Cache hit rate</div><div className="text-sm font-medium">{stats.traffic.hit_rate_percent == null ? 'n/a' : `${stats.traffic.hit_rate_percent}%`}</div></div>
                        <div><div className="text-xs text-gray-500">Evictions</div><div className={`text-sm font-medium ${stats.traffic.evicted_keys ? 'text-red-600' : ''}`}>{stats.traffic.evicted_keys.toLocaleString()}</div></div>
                    </div>
                    <div className="overflow-x-auto border border-gray-200 rounded bg-white">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50"><tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Prefix</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Keys</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Memory</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">No expiry</th>
                            </tr></thead>
                            <tbody className="divide-y divide-gray-100">
                                {stats.prefixes.map(row => <tr key={row.prefix}>
                                    <td className="px-3 py-2 text-xs font-mono">{row.prefix}</td>
                                    <td className="px-3 py-2 text-xs text-right">{row.keys.toLocaleString()}</td>
                                    <td className="px-3 py-2 text-xs text-right">{bytesLabel(row.bytes)}</td>
                                    <td className={`px-3 py-2 text-xs text-right ${row.no_expiry ? 'text-amber-700 font-medium' : ''}`}>{row.no_expiry.toLocaleString()}</td>
                                </tr>)}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Search bar */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                <div className="relative flex-1 max-w-lg">
                    <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                    <input
                        value={patternDraft}
                        onChange={e => setPatternDraft(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') runSearch(); }}
                        placeholder="Match pattern, e.g. login_cache:* or *"
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500"
                    />
                </div>
                <button onClick={runSearch} className="px-4 py-2 text-sm bg-gray-800 text-white rounded-md hover:bg-gray-900">Search</button>
                <button onClick={() => scan(true)} className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1">
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </button>
            </div>

            {error && (
                <div className="mx-6 mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> {error}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:divide-x divide-gray-100">
                {/* Key list */}
                <div className="p-6">
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Key</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">TTL</th>
                                    <th className="px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {keys.map(k => (
                                    <tr
                                        key={k.key}
                                        onClick={() => openKey(k.key)}
                                        className={`cursor-pointer hover:bg-blue-50 ${selectedKey === k.key ? 'bg-blue-50' : ''}`}
                                    >
                                        <td className="px-3 py-2 text-xs font-mono text-gray-800 break-all max-w-xs">{k.key}</td>
                                        <td className="px-3 py-2 text-xs text-gray-500">{k.type}</td>
                                        <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                                            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{ttlLabel(k.ttl)}</span>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <button
                                                onClick={e => { e.stopPropagation(); handleDelete(k.key); }}
                                                className="text-gray-400 hover:text-red-600"
                                                title="Delete key"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {keys.length === 0 && !loading && (
                                    <tr><td colSpan={4} className="px-3 py-8 text-center text-sm text-gray-400">No keys match this pattern.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-3 flex items-center justify-between text-sm text-gray-500">
                        <span>{keys.length} key(s) loaded</span>
                        {hasMore && (
                            <button onClick={() => scan(false)} disabled={loading} className="text-blue-600 hover:text-blue-800 disabled:opacity-50">
                                Load more
                            </button>
                        )}
                    </div>
                </div>

                {/* Detail / editor */}
                <div className="p-6">
                    {!selectedKey && <div className="text-sm text-gray-400 text-center py-12">Select a key to view its value.</div>}
                    {selectedKey && (
                        <div>
                            <div className="flex items-start justify-between mb-3">
                                <div className="min-w-0">
                                    <div className="text-xs font-mono text-gray-800 break-all">{selectedKey}</div>
                                    {detail && (
                                        <div className="mt-1 text-xs text-gray-500 flex flex-wrap items-center gap-2">
                                            <span className="px-1.5 py-0.5 bg-gray-100 rounded">{detail.type}</span>
                                            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{ttlLabel(detail.ttl)}</span>
                                            {detail.size_bytes != null && <span>{detail.size_bytes.toLocaleString()} bytes</span>}
                                            {detail.truncated && <span className="text-amber-600">truncated</span>}
                                        </div>
                                    )}
                                </div>
                                <button onClick={() => { setSelectedKey(null); setDetail(null); }} className="text-gray-400 hover:text-gray-600">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            {detailLoading && <div className="text-sm text-gray-400 py-8 text-center">Loading…</div>}

                            {!detailLoading && detail && (
                                <>
                                    {renderValue()}

                                    {detail.editable && detail.encoding !== 'base64' && (
                                        <div className="mt-3 flex items-end gap-3">
                                            <div>
                                                <label className="block text-xs text-gray-500 mb-1">TTL (seconds, blank = no expiry)</label>
                                                <input
                                                    value={editTtl}
                                                    onChange={e => setEditTtl(e.target.value.replace(/[^0-9]/g, ''))}
                                                    placeholder="∞"
                                                    className="w-40 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                                />
                                            </div>
                                            <button
                                                onClick={handleSave}
                                                disabled={saving}
                                                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                                            >
                                                <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
                                            </button>
                                        </div>
                                    )}

                                    {actionMsg && <div className="mt-2 text-xs text-gray-600">{actionMsg}</div>}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Create-key modal */}
            {showCreate && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
                        <h3 className="text-base font-medium text-gray-900 mb-3">New Redis key</h3>
                        <input
                            value={newKey}
                            onChange={e => setNewKey(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
                            placeholder="full key name"
                            autoFocus
                            className="w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500"
                        />
                        <p className="text-xs text-gray-500 mt-2">A string key is created when you set a value and Save.</p>
                        <div className="mt-4 flex justify-end gap-2">
                            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
                            <button onClick={handleCreate} disabled={!newKey.trim()} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">Continue</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RedisTab;
